import EntityType from '@enums/EntityType';
import { NO_PROCESS } from '@utils/entityMaps/resolveActivityProcess';
import { PropertyType } from '@/lib/enums';
import { SkillLevel } from '@/lib/enums';
import combinedItemType from './combinedEntityType';
import getMetrics from './getMetrics';

const propertyTree = (item, properties) => {
  const tree = {};

  if (!properties) {
    return tree;
  }

  let propertyType = EntityType.ACTIVITY_PROPERTY;
  let valueType = EntityType.ACTIVITY_PROPERTY_VALUE;

  if (item.type === EntityType.ROLE) {
    propertyType = EntityType.ROLE_PROPERTY;
    valueType = EntityType.ROLE_PROPERTY_VALUE;
  }

  if (item.type === EntityType.GROUP) {
    propertyType = EntityType.GROUP_PROPERTY;
    valueType = EntityType.GROUP_PROPERTY_VALUE;
  }

  // Add each property to the tree.
  properties.scoped?.get(item.type)?.list.forEach((property) => {
    if (property.type !== PropertyType.DROPDOWN) {
      return;
    }
    let isAllowed = true;

    const propertyValues = {};

    if (item.allowedIds) {
      isAllowed = false;
    }

    property.options.forEach(({ uuid }) => {
      if (item.allowedIds && !item.allowedIds.has(uuid)) {
        return;
      }

      isAllowed = true;

      propertyValues[uuid] = {
        id: uuid,
        type: valueType,
        metrics: getMetrics(),
        parentId: property.uuid,
        parentItem: {
          type: item.type,
          typeId: null,
          subType: EntityType.PROPERTY,
          subTypeId: property.uuid,
        },
      };
    });

    if (!isAllowed) {
      return;
    }

    tree[property.uuid] = {
      id: property.uuid,
      type: propertyType,
      metrics: getMetrics(),
    };

    if (!property.options.length) {
      return;
    }

    tree[property.uuid].childType = valueType;
    tree[property.uuid].children = propertyValues;
  });

  return tree;
};

const skillTree = (item, skills) => {
  const tree = {};

  if (!skills) {
    return tree;
  }

  let skillType = EntityType.ROLE_SKILL;
  let levelType = EntityType.ROLE_SKILL_LEVEL;

  if (item.type === EntityType.PERSON) {
    skillType = EntityType.PERSON_SKILL;
    levelType = EntityType.PERSON_SKILL_LEVEL;
  }

  // Add each tag to the tree.
  skills.list.forEach((skill) => {
    if (item.allowedValues && !item.allowedValues.has(skill.uuid)) {
      return;
    }

    const parentId = skill.uuid;

    tree[skill.uuid] = {
      id: skill.uuid,
      type: skillType,
      metrics: getMetrics(),
      childType: levelType,
      children: {},
    };

    const allowedLevels = item.allowedValues?.get(skill.uuid);

    if (!allowedLevels || allowedLevels.has(SkillLevel.BASIC)) {
      tree[parentId].children[SkillLevel.BASIC] = {
        id: SkillLevel.BASIC,
        parentId,
        type: levelType,
        metrics: getMetrics(),
      };
    }

    if (!allowedLevels || allowedLevels.has(SkillLevel.INTERMEDIATE)) {
      tree[parentId].children[SkillLevel.INTERMEDIATE] = {
        id: SkillLevel.INTERMEDIATE,
        parentId,
        type: levelType,
        metrics: getMetrics(),
      };
    }

    if (!allowedLevels || allowedLevels.has(SkillLevel.ADVANCED)) {
      tree[parentId].children[SkillLevel.ADVANCED] = {
        id: SkillLevel.ADVANCED,
        parentId,
        type: levelType,
        metrics: getMetrics(),
      };
    }
  });

  return tree;
};

const tagTree = (item, tags) => {
  const tree = {};

  if (!tags) {
    return tree;
  }

  let tagType = EntityType.ACTIVITY_TAG;
  let categoryType = EntityType.ACTIVITY_TAG_CATEGORY;

  if (item.type === EntityType.ROLE) {
    tagType = EntityType.ROLE_TAG;
    categoryType = EntityType.ROLE_TAG_CATEGORY;
  }

  if (item.type === EntityType.GROUP) {
    tagType = EntityType.GROUP_TAG;
    categoryType = EntityType.GROUP_TAG_CATEGORY;
  }

  let categoryIndex = 0;
  tags.ordered.__order.forEach((categoryItem, categoryIndex) => {
    tags.ordered[categoryItem].__order.forEach((categoryId) => {
      const id = categoryItem === 'system' ? 'system' : categoryId;

      if (id === 'system' && item.type !== EntityType.ACTIVITY) {
        return;
      }

      if (id !== 'system') {
        const tagSet =
          tags.ordered.custom[id].__taggables_set ||
          tags.ordered.custom[id].taggables;
        if (!tagSet.has(item.type)) {
          return;
        }
      }

      let children = null;

      tags.ordered[categoryItem][categoryId].tags.__order.forEach(
        (tagId, index) => {
          if (item.allowedIds?.size && !item.allowedIds.has(tagId)) {
            return;
          }

          if (!children) {
            children = {};
          }

          children[tagId] = {
            id: tagId,
            order: index,
            metrics: getMetrics(),
            parentId: id,
            parentItem: {
              type: item.type,
              typeId: null,
              subType: EntityType.TAG_CATEGORY,
              subTypeId: id,
            },
            type: tagType,
          };
        }
      );

      tree[id] = {
        id,
        type: categoryType,
        order: categoryIndex,
        metrics: getMetrics(),
        childType: tagType,
        children,
      };

      categoryIndex += 1;
    });
  });

  return tree;
};

const activityTree = (activities, item) => {
  const tree = {};

  if (!activities) {
    return tree;
  }

  activities.list.forEach(({ uuid }) => {
    if (item.allowedIds && !item.allowedIds.has(uuid)) {
      return;
    }

    tree[uuid] = {
      id: uuid,
      type: EntityType.ACTIVITY_TYPE,
      metrics: getMetrics(),
    };
  });

  return tree;
};

const personTree = (people, item) => {
  const tree = {};

  if (!people?.length) {
    return tree;
  }

  people.forEach((id) => {
    if (item.allowedIds && !item.allowedIds.has(id)) {
      return;
    }

    tree[id] = {
      id,
      type: EntityType.PERSON,
      metrics: getMetrics(),
    };
  });

  return tree;
};

const groupTree = (entities, order) => {
  const item = order[0];
  const nextItem = order[1];
  const lastItem = order[2];

  const tree = {};
  const groups = entities.groups;

  if (!groups) {
    return tree;
  }

  const includeRoles =
    nextItem.type === EntityType.ROLE &&
    (!nextItem.subType || nextItem.subType === EntityType.ROLE_TYPE);

  const nextType = combinedItemType(nextItem.type, nextItem.subType);

  const lastType = lastItem
    ? combinedItemType(lastItem.type, lastItem.subType)
    : null;

  Object.entries(groups).forEach(([id, group]) => {
    if (item.allowedIds && !item.allowedIds.has(id)) {
      return;
    }

    tree[id] = {
      id,
      type: EntityType.GROUP,
      metrics: getMetrics(),
      childType: nextType,
    };

    if (includeRoles) {
      const roleTree = {};
      [...group._relationships.roles].forEach((sym) => {
        const id = sym.description;

        roleTree[id] = {
          id,
          type: nextType,
          metrics: getMetrics(),
          childType: lastType,
        };
      });

      tree[id].children = roleTree;
    }
  });

  return tree;
};

const roleTree = (roles, item, childItem) => {
  const tree = {};

  const childType = combinedItemType(childItem.type, childItem.subType);

  if (!roles) {
    return tree;
  }

  Object.keys(roles).forEach((id) => {
    if (item.allowedSubIds && !item.allowedSubIds.has(id)) {
      return;
    }

    tree[id] = {
      id,
      type: EntityType.ROLE,
      metrics: getMetrics(),
      childType,
    };
  });

  return tree;
};

const processTree = (library, item, nextItem) => {
  const tree = {};
  const l1Processes = library?.processes?.l1;

  if (!l1Processes?.size) {
    return tree;
  }

  const childType = nextItem
    ? combinedItemType(nextItem.type, nextItem.subType)
    : null;

  const addEntry = (id) => {
    if (item.allowedIds && !item.allowedIds.has(id)) {
      return;
    }
    tree[id] = {
      id,
      type: EntityType.L1_PROCESS,
      metrics: getMetrics(),
    };
    if (childType) {
      tree[id].childType = childType;
    }
  };

  l1Processes.forEach((_, id) => addEntry(id));
  addEntry(NO_PROCESS);

  return tree;
};

const roleTypeTree = (roles, item) => {
  const tree = {};

  if (!roles) {
    return tree;
  }

  roles.list.forEach(({ title, hashId }) => {
    if (item.allowedIds && !item.allowedIds.has(hashId)) {
      return;
    }

    tree[title] = {
      id: title,
      type: EntityType.ROLE_TYPE,
      metrics: getMetrics(),
    };
  });

  return tree;
};

export default function initializeTree(order, library, entities, level) {
  const item = order[0];

  if (item.type == EntityType.PERSON && !item.subType) {
    // We don't want to create a library of all people in the design if we're
    // not looking at the organisation level.
    if (level.type !== EntityType.ORGANISATION) {
      return;
    }
    return personTree(Object.keys(entities.people), item);
  }

  switch (item.subType) {
    case EntityType.TAG:
      return tagTree(item, library?.tags);
    case EntityType.ROLE_TYPE:
      return roleTypeTree(library.roles, item);
    case EntityType.PROPERTY:
      return propertyTree(item, library.properties, item.allowedIds);
    case EntityType.SKILL:
      return skillTree(item, library.skills, item.allowedIds);
    default:
      if (item.type === EntityType.ROLE) {
        return roleTree(entities.roles, item, order[1]);
      }
      if (item.type === EntityType.GROUP) {
        return groupTree(entities, order);
      }
      if (item.type === EntityType.L1_PROCESS) {
        return processTree(library, item, order[1]);
      }
      return {};
  }
}
